import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ADS_COUNTRY_LIST_COUNTRIES_MAX, ADS_COUNTRY_LIST_NAME_MAX, ADS_COUNTRY_LIST_NOTE_MAX, } from '@wit-support/admin-contracts';
import { Button, Card, ConfirmDialog, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, EmptyState, ErrorCard, InlineNotice, LoadingTableCard, RowActionsMenu, Table, toast, useTableColumns, } from '@wit-support/ui';
import { Actions, CancelButton, SubmitButton, ZodField, ZodForm } from '@wit-support/ui-forms';
import { CountryListText } from '@wit-support/ui-domain';
import { AdsTime } from '../components/ads-time.js';
import { CountryCodesWidget } from '../registry/field-widget.js';
import { Globe, Plus } from 'lucide-react';
import { createCountryList, deleteCountryList, updateCountryList } from '../api.js';
import { useAdsLauncherPerms } from '../perms.js';
import { ADS_KEY, AdsScreen, describeAdsError, submitting, useCountryLists } from '../shared.js';
import { ACTIONS_COLUMN, ACTIONS_COLUMN_DEF, ColumnHeader, TableColumnsBar, adsTableColumnsKey, loadingHeaderCells, sortRows, useColumnSort, } from '../components/table-kit.js';
const COLUMNS = [
    { id: 'name', label: 'Название', hideable: false, defaultPin: 'left' },
    { id: 'countries', label: 'Страны' },
    { id: 'note', label: 'Примечание' },
    { id: 'updated', label: 'Изменён' },
    ACTIONS_COLUMN_DEF,
];
const DESC_FIRST = new Set(['countries', 'updated']);
function listSortValue(l, key) {
    switch (key) {
        case 'name':
            return l.name;
        case 'countries':
            return l.countries.length;
        case 'note':
            return l.note ?? null;
        case 'updated':
            return Date.parse(l.updatedAt);
        case ACTIONS_COLUMN:
            return null;
    }
}
export function CountryListsPage() {
    const { canManageLists } = useAdsLauncherPerms();
    const [editing, setEditing] = useState(null);
    return (_jsxs(AdsScreen, { title: "\u0421\u043F\u0438\u0441\u043A\u0438 \u0441\u0442\u0440\u0430\u043D", description: "\u0421\u043F\u0438\u0441\u043A\u0438 \u0438\u0441\u043A\u043B\u044E\u0447\u0430\u0435\u043C\u044B\u0445 \u0441\u0442\u0440\u0430\u043D. \u041E\u0431\u0449\u0438\u0435 \u0441\u043E SCALE: \u043F\u0440\u0430\u0432\u043A\u0430 \u0437\u0434\u0435\u0441\u044C \u043C\u0435\u043D\u044F\u0435\u0442 \u0438 \u0433\u0435\u043E \u0431\u0443\u0434\u0443\u0449\u0438\u0445 \u043A\u043B\u043E\u043D\u043E\u0432 SCALE.", width: "full", toolbar: canManageLists
            ? [
                {
                    label: 'Новый список',
                    priority: 'primary',
                    render: (size) => (_jsxs(Button, { type: "button", size: size, onClick: () => setEditing('new'), className: "gap-1.5", children: [_jsx(Plus, { "aria-hidden": true, className: "h-4 w-4" }), "\u041D\u043E\u0432\u044B\u0439 \u0441\u043F\u0438\u0441\u043E\u043A"] })),
                },
            ]
            : [], children: [_jsx(ListsBody, { onEdit: setEditing }), editing ? (_jsx(ListDialog, { list: editing === 'new' ? null : editing, onClose: () => setEditing(null) })) : null] }));
}
function ListsBody({ onEdit }) {
    const { canManageLists } = useAdsLauncherPerms();
    const qc = useQueryClient();
    const q = useCountryLists();
    const [deleting, setDeleting] = useState(null);
    const del = useMutation({
        mutationFn: (id) => deleteCountryList(id),
        onSuccess: () => {
            toast.success('Список удалён');
            void qc.invalidateQueries({ queryKey: ADS_KEY });
        },
        onError: (err) => toast.error(describeAdsError(err, 'write')),
    });
    const cols = useTableColumns(adsTableColumnsKey('country-lists'), COLUMNS);
    const [sort, setSort] = useColumnSort(cols);
    const rows = useMemo(() => sortRows(q.data?.items ?? [], sort, listSortValue), [q.data, sort]);
    if (q.isError)
        return _jsx(ErrorCard, { children: describeAdsError(q.error) });
    if (q.isLoading || !q.data) {
        return (_jsx(LoadingTableCard, { headerCells: loadingHeaderCells(cols.visible), rows: 3, density: "compact" }));
    }
    if (q.data.items.length === 0) {
        return (_jsx(Card, { children: _jsx(EmptyState, { icon: Globe, title: "\u0421\u043F\u0438\u0441\u043A\u043E\u0432 \u043D\u0435\u0442", description: "\u0421\u043E\u0437\u0434\u0430\u0439\u0442\u0435 \u0441\u043F\u0438\u0441\u043E\u043A \u0441\u0442\u0440\u0430\u043D \u0434\u043B\u044F \u0438\u0441\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F." }) }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex flex-col gap-2", children: [_jsx(TableColumnsBar, { columns: cols, sort: sort }), _jsx(Card, { className: "overflow-hidden", children: _jsxs(Table, { density: "compact", children: [_jsx(Table.Header, { children: _jsx(Table.Row, { children: cols.visible.map((c) => c.id === ACTIONS_COLUMN ? (_jsx(Table.HeaderCell, { column: c, className: "w-10", children: _jsx("span", { className: "sr-only", children: c.def.label }) }, c.id)) : (_jsx(ColumnHeader, { column: c, sort: sort, onSortChange: setSort, defaultDirection: DESC_FIRST.has(c.id) ? 'desc' : 'asc' }, c.id))) }) }), _jsx(Table.Body, { children: rows.map((l) => (_jsx(Table.Row, { "data-testid": `list-${l.id}`, children: cols.visible.map((c) => (_jsx(ListCell, { column: c, list: l, canManage: canManageLists, onEdit: onEdit, onDelete: setDeleting }, c.id))) }, l.id))) })] }) })] }), _jsx(ConfirmDialog, { open: deleting != null, onOpenChange: (o) => (!o ? setDeleting(null) : null), title: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C \u0441\u043F\u0438\u0441\u043E\u043A?", description: `«${deleting?.name ?? ''}» используется и правилами SCALE. Шаблоны, которые на него ссылаются, перестанут проходить проверку.`, cancelLabel: "\u041E\u0442\u043C\u0435\u043D\u0430", actionLabel: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C", actionStyle: "danger", onConfirm: () => {
                    if (deleting)
                        del.mutate(deleting.id);
                    setDeleting(null);
                } })] }));
}
function ListCell({ column: c, list: l, canManage, onEdit, onDelete, }) {
    switch (c.id) {
        case 'name':
            return (_jsx(Table.Cell, { column: c, className: "font-medium", children: l.name }));
        case 'countries':
            return (_jsxs(Table.Cell, { column: c, className: "text-sm", children: [_jsxs("span", { className: "tabular-nums text-[var(--color-text-muted)]", children: [l.countries.length, ":"] }), ' ', _jsx(CountryListText, { codes: l.countries, max: 8 })] }));
        case 'note':
            return (_jsx(Table.Cell, { column: c, className: "text-sm text-[var(--color-text-secondary)]", children: l.note ?? '—' }));
        case 'updated':
            return (_jsx(Table.Cell, { column: c, children: _jsx(AdsTime, { iso: l.updatedAt, preset: "dateTimeNumeric" }) }));
        case ACTIONS_COLUMN:
            return (_jsx(Table.Cell, { column: c, children: canManage ? (_jsxs(RowActionsMenu, { "aria-label": "\u0414\u0435\u0439\u0441\u0442\u0432\u0438\u044F \u0441\u043E \u0441\u043F\u0438\u0441\u043A\u043E\u043C", children: [_jsx(RowActionsMenu.Item, { onSelect: () => onEdit(l), children: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C" }), _jsx(RowActionsMenu.DangerItem, { onSelect: () => onDelete(l), children: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C" })] })) : null }));
    }
}
const ListSchema = z.object({
    name: z
        .string()
        .trim()
        .min(1, { message: 'Название обязательно' })
        .max(ADS_COUNTRY_LIST_NAME_MAX)
        .meta({ label: 'Название' }),
    countries: z
        .array(z.string())
        .min(1, { message: 'Нужна хотя бы одна страна' })
        .max(ADS_COUNTRY_LIST_COUNTRIES_MAX)
        .superRefine((codes, ctx) => {
        const bad = codes.filter((c) => !/^[A-Za-z]{2}$/.test(c.trim()));
        if (bad.length > 0)
            ctx.addIssue({ code: 'custom', message: `Не коды ISO: ${bad.join(', ')}` });
    })
        .meta({
        label: 'Страны',
        description: 'Поиск по названию или коду; можно вставить список кодов: DE, FR, IT.',
        placeholder: 'Страна или код…',
        maxCountries: ADS_COUNTRY_LIST_COUNTRIES_MAX,
    }),
    note: z
        .string()
        .max(ADS_COUNTRY_LIST_NOTE_MAX)
        .optional()
        .meta({ label: 'Примечание', widget: 'textarea' }),
});
function ListDialog({ list, onClose }) {
    const qc = useQueryClient();
    const save = useMutation({
        mutationFn: (v) => {
            const countries = [...new Set(v.countries.map((c) => c.trim().toUpperCase()))];
            const note = v.note?.trim();
            return list
                ? updateCountryList(list.id, {
                    name: v.name,
                    countries,
                    ...(note !== undefined ? { note } : {}),
                })
                : createCountryList({ name: v.name, countries, ...(note ? { note } : {}) });
        },
        onSuccess: () => {
            toast.success(list ? 'Список сохранён' : 'Список создан');
            void qc.invalidateQueries({ queryKey: ADS_KEY });
            onClose();
        },
        onError: (err) => toast.error(describeAdsError(err, 'write')),
    });
    return (_jsx(Dialog, { open: true, onOpenChange: (o) => (!o ? onClose() : null), children: _jsxs(DialogContent, { children: [_jsxs(DialogHeader, { children: [_jsx(DialogTitle, { children: list ? 'Изменить список' : 'Новый список стран' }), _jsx(DialogDescription, { children: "\u0421\u043F\u0438\u0441\u043E\u043A \u043E\u0431\u0449\u0438\u0439 \u0441\u043E SCALE." })] }), list ? (_jsx(InlineNotice, { variant: "warning", as: "div", children: "\u041F\u0440\u0430\u0432\u043A\u0430 \u043C\u0435\u043D\u044F\u0435\u0442 \u0433\u0435\u043E \u0431\u0443\u0434\u0443\u0449\u0438\u0445 \u043A\u043B\u043E\u043D\u043E\u0432 SCALE, \u043A\u043E\u0442\u043E\u0440\u044B\u0435 \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u044E\u0442 \u044D\u0442\u043E\u0442 \u0441\u043F\u0438\u0441\u043E\u043A." })) : null, _jsxs(ZodForm, { schema: ListSchema, defaultValues: {
                        name: list?.name ?? '',
                        countries: list?.countries ?? [],
                        note: list?.note ?? '',
                    }, onSubmit: (v) => submitting(save.mutateAsync(v)), children: [_jsx(ZodField, { name: "name" }), _jsx(ZodField, { name: "countries", as: CountryCodesWidget }), _jsx(ZodField, { name: "note" }), _jsxs(Actions, { children: [_jsx(CancelButton, { onClick: onClose }), _jsx(SubmitButton, { pendingLabel: "\u0421\u043E\u0445\u0440\u0430\u043D\u044F\u0435\u043C\u2026", children: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C" })] })] })] }) }));
}
//# sourceMappingURL=country-lists.js.map